// Walkthroughs for the phone app's pages (docs/clairvoyance/18-help-and-training.md).
//
// The phone app has no menu, so each walkthrough says itself who can open its page (`anyOf`):
// the permissions that show its tab or button and that its commands need (05 §5.2). Steps about
// something only some of those people can do carry their own `anyOf`.
import type { Permission, Walkthrough } from '@ecobox/domain';

const LOADERS: readonly Permission[] = ['trip.load', 'container.load'];
const RECEIVERS: readonly Permission[] = ['trip.unload'];
const DRIVERS: readonly Permission[] = ['finalmile.deliver', 'pickup.complete'];

const today: Walkthrough = {
  id: 'ops-today',
  app: 'ops',
  pageTitle: 'Today',
  summary: 'Your start page: what is waiting on this phone and the work in front of you.',
  version: 1,
  steps: [
    {
      title: 'What Today is for',
      body: 'Today is your start page. It shows what this phone is still sending and the work waiting for you. You see only the parts your job needs.',
    },
    {
      target: 'sync-tiles',
      title: 'What is waiting to sync',
      body: 'With no signal, keep working: every scan and delivery is saved on the phone and sent when the signal returns. Waiting to sync counts what is still on the phone. Rejected means the server said no.',
    },
    {
      target: 'cash-held',
      title: 'Cash I hold',
      body: 'The cash you took at the door, until you hand it to the cashier. It should be zero when you start the day.',
      anyOf: DRIVERS,
    },
    {
      target: 'my-runs',
      title: 'My runs',
      body: 'Your runs for today, with how many stops are done. Tap Open the run to see the stops in order, or Continue the run once it has started.',
      anyOf: DRIVERS,
    },
    {
      target: 'today-actions',
      title: 'Load',
      body: 'Tap Load to scan boxes onto a truck trip or into a container at this branch.',
      anyOf: LOADERS,
    },
    {
      target: 'today-actions',
      title: 'Receive',
      body: 'Tap Receive to scan boxes off a trip or a container that has arrived here.',
      anyOf: RECEIVERS,
    },
    {
      target: 'today-actions',
      title: 'Container yard',
      body: 'Container yard records a container’s steps outside the hub: empty pick-up, gate-in, pull-out, arrival and empty return.',
      anyOf: ['container.manage', 'trip.unload'],
    },
    {
      target: 'today-actions',
      title: 'Report a problem',
      body: 'Found a damaged, missing or extra box? Tap Report a problem. It works with no signal.',
      anyOf: ['exception.raise'],
    },
    {
      target: 'my-sessions',
      title: 'My sessions in progress',
      body: 'A scanning session you started and did not finish stays here, even after the phone restarts. Tap Continue scanning to pick it up where you left it.',
    },
    {
      target: 'branch-board',
      title: 'At your branch',
      body: 'Trips and containers at your branch, the one waiting longest first. The edge turns amber when one is close to late and red once it is late.',
      anyOf: [...LOADERS, ...RECEIVERS],
    },
    {
      title: 'Before you start',
      body: 'Working at another branch today? Change the branch at the top or in Me. If a counter stays above zero for long, open Me to see each item and resend it.',
    },
  ],
};

const load: Walkthrough = {
  id: 'ops-load',
  app: 'ops',
  pageTitle: 'Load',
  summary: 'Open or choose a trip or container and scan the boxes onto it.',
  version: 1,
  anyOf: LOADERS,
  steps: [
    {
      title: 'What Load is for',
      body: 'Load is where you scan boxes onto a truck trip or into a container at the branch you work at.',
    },
    {
      target: 'load-mode',
      title: 'Trip or container',
      body: 'If your job includes both, choose Trip or Container here first.',
      anyOf: ['container.load'],
    },
    {
      target: 'new-trip',
      title: 'Open a new trip',
      body: 'Choose the branch it is Going to, and the vehicle and driver if you know them. Open trip and start scanning takes you straight to the scanner, even with no signal.',
      anyOf: ['trip.load'],
    },
    {
      target: 'open-trips',
      title: 'Trips being loaded',
      body: 'A trip that is already open is listed here with how many boxes are on it. Tap Scan onto this trip to add more boxes to it.',
      anyOf: ['trip.load'],
    },
    {
      target: 'load-containers',
      title: 'Containers to load',
      body: 'Containers open for loading at this branch. A container is planned in the office first. Tap Scan into this container to start.',
      anyOf: ['container.load'],
    },
    {
      title: 'Only paid boxes leave the country',
      body: 'A trip may carry a box that is not paid yet, but a container takes only boxes paid in full. The scanner warns you at once. Leave such a box where it is and tell your supervisor. A session you left open is at the top of this page.',
    },
  ],
};

const receive: Walkthrough = {
  id: 'ops-receive',
  app: 'ops',
  pageTitle: 'Receive',
  summary: 'Scan boxes off the trips and containers that arrive at this branch.',
  version: 1,
  anyOf: RECEIVERS,
  steps: [
    {
      title: 'What Receive is for',
      body: 'Receive is where you scan boxes off the trucks and containers that arrive at the branch you work at.',
    },
    {
      target: 'inbound-trips',
      title: 'Trips coming here',
      body: 'Each trip on its way shows how many boxes were loaded and how many are received so far. When it is being unloaded, tap Scan boxes off this trip.',
    },
    {
      target: 'inbound-containers',
      title: 'Containers for this branch',
      body: 'A container out of the port can be devanned: tap Devan: scan boxes out. One that is not at the hub yet has its steps recorded under Container yard.',
    },
    {
      title: 'Sessions you started',
      body: 'A receiving session you started stays at the top of this page until you close it, even if the phone restarts.',
    },
    {
      title: 'Scan everything before closing',
      body: 'Scan every box before you close. Closing raises each expected box that was not scanned as missing, for a person to follow up.',
    },
  ],
};

const session: Walkthrough = {
  id: 'ops-session',
  app: 'ops',
  pageTitle: 'Scanning session',
  summary: 'Scan many boxes onto or off one trip or container, then finish.',
  version: 1,
  anyOf: [...LOADERS, ...RECEIVERS],
  steps: [
    {
      target: 'session-head',
      title: 'One trip or container',
      body: 'This session is for the trip or container named at the top. Scan every box, then finish.',
    },
    {
      target: 'session-counters',
      title: 'The counters',
      body: 'Loading shows boxes scanned and not sent yet. Receiving shows expected, scanned, missing and extra. A box that is not on the list is still received, and counted as extra.',
    },
    {
      target: 'scan-field',
      title: 'Scan with a scanner, or type',
      body: 'The cursor stays in this box. A Bluetooth or USB scanner types the barcode and Enter for you, or you can type it. A box scanned twice counts once.',
    },
    {
      target: 'scan-camera',
      title: 'Scan with the camera',
      body: 'Tap Scan with the camera and point it at each label. It beeps and buzzes on every read. If the camera does not read, type the barcode or clean the lens.',
    },
    {
      target: 'scan-list',
      title: 'Every scan and its state',
      body: 'The newest scan is on top with its state: not sent yet, waiting to sync, done, or refused and why.',
    },
    {
      target: 'scan-list',
      title: 'Held back boxes',
      body: 'A box the phone knows will be refused (not paid for a container, on hold, wrong branch) is held back with the reason. Do not load it: tell your supervisor. Remove or Take off takes back a box scanned by mistake.',
      anyOf: LOADERS,
    },
    {
      target: 'session-counters',
      title: 'No signal? Keep scanning',
      body: 'Scans are saved on the phone and sent when the signal returns. Not sent yet shows what is still waiting. Keep the phone on and do not clear the app.',
    },
    {
      target: 'finish-scanning',
      title: 'Finish scanning',
      body: 'After the last box, tap Finish scanning. On the next card you can still choose Keep scanning, or Leave it open for now.',
    },
    {
      title: 'Depart the trip',
      body: 'For a trip you are loading, tap Depart the trip now when the truck leaves.',
      anyOf: ['trip.load'],
    },
    {
      title: 'Finish a container',
      body: 'If you may seal it, type the Seal number and tap Close and seal the container. If not, tap I am done loading: a manager closes and seals it.',
      anyOf: ['container.load', 'container.manage'],
    },
    {
      title: 'Close what you received',
      body: 'When receiving, tap Close the trip or Complete the receipt. Every expected box not scanned is raised as missing for a person to follow up.',
      anyOf: RECEIVERS,
    },
    {
      title: 'Come back to it later',
      body: 'A session stays on this phone until you finish it, even after a restart. Find it on Today under My sessions in progress.',
    },
  ],
};

const handover: Walkthrough = {
  id: 'ops-handover',
  app: 'ops',
  pageTitle: 'Hand over to a partner',
  summary: 'Choose the partner who is taking boxes and start a handover.',
  version: 1,
  anyOf: ['partner.handover'],
  steps: [
    {
      title: 'What Hand over is for',
      body: 'Here you give boxes to a partner hauler or courier. You scan what goes on their vehicle and their representative signs for it on this phone.',
    },
    {
      target: 'handover-partners',
      title: 'Who is taking the boxes?',
      body: 'Each partner shows how many boxes here are ready for them and how long the oldest has waited. Boxes not paid in full or on hold are counted as not allowed yet.',
    },
    {
      target: 'partner-list',
      title: 'Start a handover',
      body: 'Tap Start a handover on the partner whose vehicle is here. You go straight to scanning. It works with no signal.',
    },
    {
      target: 'handovers-other',
      title: 'Open on another phone',
      body: 'A handover started on another phone and not signed yet is listed here. Tap Continue here to finish it on this phone.',
    },
    {
      target: 'handovers-mine',
      title: 'Your handover in progress',
      body: 'A handover you started stays at the top with Continue until it is signed, even if the phone restarts.',
    },
  ],
};

const handoverSession: Walkthrough = {
  id: 'ops-handover-session',
  app: 'ops',
  pageTitle: 'Hand over',
  summary:
    'Scan the units onto the partner’s vehicle, note damage, and have the representative sign.',
  version: 1,
  anyOf: ['partner.handover'],
  steps: [
    {
      title: 'One handover',
      body: 'Scan every unit that goes on the partner’s vehicle, note any damage, then the representative signs on this phone.',
    },
    {
      target: 'handover-counters',
      title: 'The counters',
      body: 'Scanned, not sent yet and damaged. Units stay on the phone until you finish scanning, so a damage note goes with its unit.',
    },
    {
      target: 'handover-waiting',
      title: 'Waiting for this partner',
      body: 'Add all ready boxes adds the whole waiting list in one tap. Or scan each box as it goes on the vehicle: scanning is how you check each one.',
    },
    {
      target: 'scan-field',
      title: 'Scan or type',
      body: 'Scan the box barcode or the Prime piece label with a scanner, or type it. A unit the phone knows will be refused is held back with the reason.',
    },
    {
      target: 'scan-camera',
      title: 'Scan with the camera',
      body: 'Tap Scan with the camera and point it at each label. It beeps on every read. If it does not read, type the code.',
    },
    {
      target: 'scan-list',
      title: 'Note damage',
      body: 'For a damaged box tap Note damage and say what is wrong before you finish. The representative sees it before signing.',
    },
    {
      target: 'finish-sign',
      title: 'Finish scanning and sign',
      body: 'When everything is on the vehicle, tap Finish scanning and sign.',
    },
    {
      title: 'The representative signs',
      body: 'Write the representative’s name and the vehicle plate, take a photo of the loaded vehicle, and type the units they counted. Their count must equal the units loaded. They sign on the pad.',
    },
    {
      title: 'Signing with no signal',
      body: 'Signing works with no signal: the signature and the photo are saved on the phone and upload later. A unit the server refuses stays here and is listed with the reason.',
    },
    {
      title: 'After signing',
      body: 'Tap the Hand over button with the partner’s name. The office sends the manifest and the partner’s update link. Tap Done to go back.',
    },
  ],
};

const run: Walkthrough = {
  id: 'ops-run',
  app: 'ops',
  pageTitle: 'Run',
  summary: 'The stops of one run in order, with its progress and the cash collected.',
  version: 1,
  anyOf: DRIVERS,
  steps: [
    {
      title: 'Your run',
      body: 'This is one run: the stops in order, what is done, and the cash collected so far.',
    },
    {
      target: 'run-progress',
      title: 'Progress',
      body: 'Stops left, done and failed, and the cash collected on this run.',
    },
    {
      target: 'run-start',
      title: 'Start with a load scan',
      body: 'Tap Scan the load and start, then scan every box as it goes on the van. A run with no boxes to load, such as a run of pickups, has Start the run instead. A run with deliveries needs a driver who delivers.',
      anyOf: DRIVERS,
    },
    {
      target: 'run-stops',
      title: 'The stops',
      body: 'The next stop is marked. Each has Navigate to open the map and Call to phone the customer. At the door, tap Open the delivery or Open the pickup.',
    },
    {
      target: 'run-stops',
      title: 'No signal on the road',
      body: 'Everything you do on a run is saved on the phone and sent when there is signal. A stop says waiting to sync until the server has it.',
    },
    {
      target: 'run-end',
      title: 'End the run',
      body: 'Back at the branch, tap End the run to scan back the boxes that came back with you and see the cash to hand to the cashier.',
      anyOf: DRIVERS,
    },
    {
      title: 'The right branch',
      body: 'The run sets the phone to the run’s branch. If a red line says the branch is wrong, change it in Me before you send anything.',
    },
  ],
};

const runLoad: Walkthrough = {
  id: 'ops-run-load',
  app: 'ops',
  pageTitle: 'Load the van',
  summary: 'Scan every box as it goes on the van, then start the run.',
  version: 1,
  anyOf: ['finalmile.deliver'],
  steps: [
    {
      title: 'Load the van',
      body: 'Scan every box as it goes on the van. Then start the run.',
    },
    {
      target: 'load-counters',
      title: 'Planned and scanned',
      body: 'Planned is what the office put on this run. A warning names the stops whose boxes you have not scanned: those boxes stay at the branch and the stop has nothing to deliver.',
    },
    {
      target: 'scan-field',
      title: 'Scan or type',
      body: 'Scan each label with a scanner, or type the barcode. The cursor stays in the box.',
    },
    {
      target: 'scan-camera',
      title: 'Scan with the camera',
      body: 'Tap Scan with the camera and point it at each label. It beeps on every read.',
    },
    {
      target: 'scan-list',
      title: 'A box not planned for you',
      body: 'Every scan is listed. A box that is not planned on this run is held back. Tap Send anyway only if it really goes on this van; otherwise tap Remove.',
    },
    {
      target: 'load-start',
      title: 'Start the run',
      body: 'Tap Start the run with the number of boxes shown. It is saved at once and sent when you have signal, so you can drive off.',
    },
    {
      target: 'load-start',
      title: 'Nothing to load?',
      body: 'If no box goes on the van, tap Start without scanning any box and confirm. Every delivery stop will then have nothing to deliver.',
    },
    {
      title: 'Next: your first stop',
      body: 'The run opens again with your stops. Drive to the first one and open it there.',
    },
  ],
};

const runStop: Walkthrough = {
  id: 'ops-run-stop',
  app: 'ops',
  pageTitle: 'Stop',
  summary:
    'One stop of a run: deliver the boxes, or take a pickup, or say why it could not be done.',
  version: 2,
  anyOf: DRIVERS,
  steps: [
    {
      title: 'One stop',
      body: 'One stop of your run: a delivery or a pickup. Work down the page from the top.',
    },
    {
      target: 'stop-arrive',
      title: 'I have arrived',
      body: 'Tap I have arrived when you get there. Tick Tell the customer I am arriving soon first if they should get a message.',
    },
    {
      target: 'stop-boxes',
      title: 'Scan each box',
      body: 'Scan each box for this stop with the camera, a scanner or by typing. A box for another stop is refused. You may deliver some of the boxes; the rest come back with you.',
      anyOf: ['finalmile.deliver'],
    },
    {
      target: 'stop-code',
      title: 'Ask for the delivery code',
      body: 'Ask the receiver for the 4-digit delivery code EcoBox sent them. Never guess. No code? Tick The receiver has no code and take a photo of their ID. A wrong code typed offline shows only later, so use the photo when unsure.',
      anyOf: ['finalmile.deliver'],
    },
    {
      target: 'stop-receiver',
      title: 'Who received it',
      body: 'Write the receiver’s name and who they are to the consignee. Take photos of the delivered boxes and get a signature: at least one of the two is needed.',
      anyOf: ['finalmile.deliver'],
    },
    {
      target: 'stop-money',
      title: 'Money to collect',
      body: 'When there are destination charges, enter the Amount collected and whether it was paid by cash or bank transfer. Cash stays in your name until you hand it to the cashier.',
      anyOf: ['finalmile.deliver'],
    },
    {
      target: 'stop-actions',
      title: 'Delivered, or not',
      body: 'Tap Delivered to save the delivery. If you could not deliver, tap I could not deliver, choose what happened, and add a photo of the door or a note.',
      anyOf: ['finalmile.deliver'],
    },
    {
      target: 'pickup-steps',
      title: 'A pickup, step by step',
      body: 'A pickup has up to seven short screens: Customer, Empty boxes, Boxes, Consignee, Contents, Payment, and Photos and signature. This line says which one you are on.',
      anyOf: ['pickup.complete'],
    },
    {
      target: 'stop-actions',
      title: 'Next, then Save the pickup',
      body: 'Fill in the screen and tap Next. If something is missing, the list above the button says what. On the last screen tap Save the pickup. Could not collect? Tap I could not collect here.',
      anyOf: ['pickup.complete'],
    },
    {
      title: 'The tracking number',
      body: 'Boxes shows the tracking number of this pickup at the top: write it on each box and tell the customer. It and the receipt number come from this phone, so a pickup works with no signal. On Contents, read the prohibited-items reminder to the customer.',
      anyOf: ['pickup.complete'],
    },
    {
      title: 'No signal at the door',
      body: 'Carry on. Photos and signatures are saved on the phone and upload later. The stop says waiting to sync until the server has it.',
    },
    {
      title: 'Back to the run',
      body: 'When the stop is saved, tap The run at the top to go back to your stops.',
    },
  ],
};

const runEnd: Walkthrough = {
  id: 'ops-run-end',
  app: 'ops',
  pageTitle: 'End the run',
  summary: 'Scan back what was not delivered, end the run and hand the cash to the cashier.',
  version: 1,
  // closeRun takes either driver permission (04 §4.5): a pickup driver ends their run too.
  anyOf: DRIVERS,
  steps: [
    {
      title: 'End the run',
      body: 'End the run when you are back at the branch: scan back every box you did not deliver, then hand the cash to the cashier.',
    },
    {
      target: 'scan-back',
      title: 'Boxes to scan back',
      body: 'To come back counts every box you loaded and did not deliver. The list shows the ones still out and their stop.',
    },
    {
      target: 'scan-field',
      title: 'Scan each box back',
      body: 'Scan each box with a scanner or type the barcode, or tap Scan with the camera. A box that is not on the list is still taken, and flagged.',
    },
    {
      target: 'cash-handover',
      title: 'Cash to hand to the cashier',
      body: 'The cash to put in the cashier’s hand: what you took on this run plus what is already held in your name, by stop. Give it to the cashier and have them confirm.',
    },
    {
      target: 'end-note',
      title: 'Note for the office',
      body: 'A box missing from the van? Scan back what you have and write what happened here.',
    },
    {
      target: 'end-actions',
      title: 'End the run',
      body: 'Tap End the run. If stops are still open or boxes are not back, the phone warns you and asks again. Boxes not scanned back are raised as missing.',
    },
    {
      title: 'Works with no signal',
      body: 'Ending the run is saved on the phone and sent when you have signal. If the server refuses it, the page says why and you can scan back again.',
    },
    {
      title: 'After the run',
      body: 'Cash I hold on Today shows the cash in your name until the cashier confirms they received it.',
    },
  ],
};

const yard: Walkthrough = {
  id: 'ops-yard',
  app: 'ops',
  pageTitle: 'Container yard',
  summary: 'Record a container’s steps outside the hub, with the time each happened.',
  version: 1,
  anyOf: ['container.manage', 'trip.unload'],
  steps: [
    {
      title: 'What Container yard is for',
      body: 'Record each step of a container outside the hub: empty pick-up, gate-in at the port, pull-out, arrival at the hub and empty return.',
    },
    {
      target: 'yard-find',
      title: 'Find a container',
      body: 'Type the container, booking or BL number to narrow the list.',
    },
    {
      target: 'yard-list',
      title: 'Record a step',
      body: 'Each container shows its status. Tap Record a step on the one you want.',
    },
    {
      target: 'yard-container',
      title: 'The steps you may record',
      body: 'You see only the steps your job allows, and the usual next step stands out. Set When to the time it really happened, then tap its Record button.',
    },
    {
      title: 'Empty pick-up and return',
      body: 'Empty picked up needs the container number. Empty returned takes the depot and stops the detention clock.',
      anyOf: ['container.manage'],
    },
    {
      title: 'Arrived at the hub',
      body: 'Record Arrived at the hub when the container is at the hub gate. It starts the devanning clock.',
      anyOf: ['trip.unload'],
    },
    {
      title: 'Saved with no signal',
      body: 'Each step is saved on the phone and sent when you have signal. A time in the future is refused. Tap Choose another container to go back to the list.',
    },
  ],
};

const exception: Walkthrough = {
  id: 'ops-exception',
  app: 'ops',
  pageTitle: 'Report a problem',
  summary: 'Report a damaged, missing or misplaced box, or a problem with a trip or container.',
  version: 1,
  anyOf: ['exception.raise'],
  steps: [
    {
      title: 'What this page is for',
      body: 'Report a problem with a box, a shipment, a trip or a container. A person at the branch picks it up.',
    },
    {
      target: 'problem-type',
      title: 'What is wrong?',
      body: 'Choose the kind of problem: damaged, missing, an extra box, a box for another destination, and so on.',
    },
    {
      target: 'problem-subject',
      title: 'It is about',
      body: 'Choose a box, a shipment, a trip or a container. For a shipment type the tracking number; for a trip or container pick it from the list.',
    },
    {
      target: 'problem-box',
      title: 'Scan the box',
      body: 'For a box, scan it with a scanner, type the barcode, or tap Scan with the camera.',
    },
    {
      target: 'problem-description',
      title: 'What happened?',
      body: 'Say what you saw in a few plain words. This is needed.',
    },
    {
      target: 'problem-photos',
      title: 'Photos',
      body: 'Take up to 10 photos. They are made smaller and upload when you have signal; the report does not wait for them.',
    },
    {
      target: 'problem-send',
      title: 'Report the problem',
      body: 'Tap Report the problem. It is saved at once, even with no signal, and the page is ready for the next one.',
    },
  ],
};

const scan: Walkthrough = {
  id: 'ops-scan',
  app: 'ops',
  pageTitle: 'Scan',
  summary: 'Look up any box: what it is, where it is, its shipment and what you can do with it.',
  version: 1,
  // Everyone has the Scan tab (05 §5.2): it only reads, as far as each person may see.
  steps: [
    {
      title: 'What Scan is for',
      body: 'Scan any box to see what it is, where it is, its shipment and what you can do with it now. Scan changes nothing: it only looks the box up.',
    },
    {
      target: 'scan-field',
      title: 'Scan or type',
      body: 'Scan with a scanner, tap Scan with the camera, or type the label and press Enter. A box label looks like EB0000012344; a Prime piece label works too.',
    },
    {
      target: 'scan-box',
      title: 'The box',
      body: 'Its status, where it is and its last scan. Red lines say what is wrong: on hold, damaged, not accounted for, not paid in full, or not at your branch.',
    },
    {
      target: 'scan-shipment',
      title: 'Its shipment',
      body: 'The tracking number, who it goes to and whether it is paid. If it says the details are not visible to you, the shipment belongs to a branch outside your access.',
    },
    {
      target: 'scan-actions',
      title: 'What you can do',
      body: 'What you can do with the box now and on which tab. A line marked Refused says why, such as not paid in full or on hold: leave the box where it is and tell your supervisor.',
      anyOf: [...LOADERS, ...RECEIVERS, 'partner.handover'],
    },
    {
      target: 'scan-actions',
      title: 'Report a problem',
      body: 'Damaged, wet or open? Tap Report a problem: the form opens with this box filled in.',
      anyOf: ['exception.raise'],
    },
    {
      target: 'scan-history',
      title: 'Scans at this branch',
      body: 'Every scan of the box at your branch, newest first. Scanned twice or Flagged for review means a person should look.',
      anyOf: [...LOADERS, ...RECEIVERS, 'partner.handover'],
    },
    {
      title: 'No signal',
      body: 'With no signal the phone shows what it already has, marked From this phone’s copy. A box this phone has never seen says so: try again when the signal returns.',
    },
  ],
};

const me: Walkthrough = {
  id: 'ops-me',
  app: 'ops',
  pageTitle: 'Me',
  summary: 'Your profile and branch, what this phone has sent, and sign out.',
  version: 1,
  steps: [
    {
      title: 'What Me is for',
      body: 'Me holds your profile, the branch you work at, what this phone has sent, and Sign out.',
    },
    {
      target: 'me-profile',
      title: 'Branch and theme',
      body: 'Your name and roles are at the top. Change Branch when you work at another branch. The Light (sunlight) theme is easier to read outdoors.',
    },
    {
      target: 'me-cash',
      title: 'Cash in my custody',
      body: 'The cash held in your name, by branch, until the cashier confirms they received it.',
      anyOf: DRIVERS,
    },
    {
      target: 'me-sync',
      title: 'Sync details',
      body: 'The last things this phone sent, each with its state. Not delivered has Resend. A rejected one shows the server’s reason and Dismiss.',
    },
    {
      target: 'me-signout',
      title: 'Sign out',
      body: 'You can sign out only when nothing is waiting to sync, so no work is lost: go online first. Never use someone else’s account.',
    },
    {
      title: 'Need help?',
      body: 'If something stays rejected or will not send, show this page to your supervisor.',
    },
  ],
};

export const OPS_WALKTHROUGHS: readonly Walkthrough[] = [
  today,
  load,
  receive,
  session,
  handover,
  handoverSession,
  run,
  runLoad,
  runStop,
  runEnd,
  yard,
  exception,
  scan,
  me,
];
